import { Search } from "react-feather";
import { Button, CardBody, Input, Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import PaginationComponent from "Component/Learning/Common/Pagination";
import { setFilters } from "Features/Learning/Memberships/membershipSlice";
import { handleBackPagination, handleClickPagination, handleNextPagination } from "Component/Learning/Membership/MembershipsUtils";
import { fetchMemberships } from "Features/Learning/Memberships/Utils/MembershipUtils";
import { setField } from "Features/Learning/Vouchers/addVoucherSlice";
import Swal from 'sweetalert2';

const ModalAddMembership = ({ handleOpenMembership, openMembership, selectedValue, setSelectedValue, handleSelectChange }) => {
  const [dataMembership, setDataMembership] = useState([]);
  const [refatch, setRefatch] = useState(false);
  const [search, setSearch] = useState('');

  const dispatch = useDispatch();
  const { filters, isLoading, data } = useSelector(state => ({
    filters: state.Membership.filters,
    isLoading: state.Membership.isLoading,
    data: state.Membership.data,
  }));
  const voucherData = useSelector(state => state.addVoucher);
  const { pagination } = data;

  const selectedIds = useMemo(() => selectedValue.map(item => item.id), [selectedValue]);

  const isSelectAllOnPage = useMemo(() => {
    if (dataMembership.length === 0) return false;
    return dataMembership.every(item => selectedIds.includes(item.id));
  }, [selectedIds, dataMembership]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await dispatch(fetchMemberships({ ...filters, sort: 'title' }));
        setDataMembership(response?.payload?.data || []);
      } catch (error) {
        console.error('Error fetching options:', error);
      }
    };
    fetchData();
  }, [dispatch, filters]);

  const updateSearchParams = useCallback(() => {
    dispatch(
      setFilters({
        ...filters,
        "filter[title]": search,
        "page": 1
      })
    );
  }, [search, dispatch, filters]);

  useEffect(() => {
    const delay = setTimeout(() => {
      if (search !== (filters["filter[title]"] || "")) {
        updateSearchParams();
      }
    }, 500);
    return () => clearTimeout(delay);
  }, [search, updateSearchParams, filters]);

  const handleSelectAll = () => {
    if (isSelectAllOnPage) {
      // Hapus semua item dari halaman ini yang ada di `selectedValue`
      const pageIds = dataMembership.map(item => item.id);
      setSelectedValue(currentSelected => currentSelected.filter(item => !pageIds.includes(item.id)));
    } else {
      // Tambahkan item dari halaman ini yang belum ada di `selectedValue`
      const newItems = dataMembership.filter(item => !selectedIds.includes(item.id));
      setSelectedValue(currentSelected => [...currentSelected, ...newItems]);
    }
  };

  const handleCheckboxChange = (item) => {
    if (selectedIds.includes(item.id)) {
      setSelectedValue(currentSelected => currentSelected.filter(value => value.id !== item.id));
    } else {
      setSelectedValue(currentSelected => [...currentSelected, item]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newSelectedValue = selectedValue.filter((value, index, self) => index === self.findIndex((t) => t.id === value.id));
    dispatch(setField({ field: 'membership', value: newSelectedValue }));
    handleSelectChange(newSelectedValue);
    handleOpenMembership(false);
  };

  const toggleModal = async () => {
    const items = Array.isArray(voucherData?.membership) ? voucherData.membership : [];
    const initialIds = (items || []).map((i) => i.id).sort().join(",");
    const currentIds = (selectedIds || []).slice().sort().join(",");

    if (openMembership) {
      // closing: jika berubah, minta konfirmasi
      if (initialIds !== currentIds) {
        const result = await Swal.fire({
          title: "Apakah anda yakin?",
          text: "Perubahan yang belum disimpan akan dibatalkan.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          confirmButtonText: "Ya",
          cancelButtonColor: "#d33",
        });
        if (!result.isConfirmed) return;
      }

      // restore ke nilai awal dan tutup modal
      setSelectedValue(items?.length > 0 ? items : []);
      handleOpenMembership(false);
      return;
    }

    // opening: inisialisasi selected dari voucherData dan buka modal
    setSelectedValue(items?.length > 0 ? items : []);
    handleOpenMembership(true);
  };

  return (
    <Modal isOpen={openMembership} size="lg" toggle={toggleModal}>
      <ModalHeader toggle={toggleModal}>
        Tambahkan Memberships ke Voucher
      </ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <strong>Terpilih:</strong> {selectedIds.length || 0} item
        </div>
        <div className="faq-form">
          <Input
            className="form-control"
            type="text"
            placeholder="Cari membership..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Search className="search-icon" />
        </div>
        <Table className="table-responsive table-bordered my-3">
          <thead className="table-light">
            <tr>
              <th scope="col">{"#"}</th>
              <th scope="col">{"ID"}</th>
              <th scope="col">Nama Memberships</th>
              <th scope="col" className="text-center" style={{ width: "50px" }}>
                <Input
                  className="checkbox_animated"
                  id="selectAllOnPage"
                  type="checkbox"
                  title="Pilih semua di halaman ini"
                  checked={isSelectAllOnPage}
                  disabled={dataMembership.length === 0}
                  onChange={handleSelectAll}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {dataMembership.length > 0 ? (
              dataMembership.map((item, index) => (
                <tr key={item.id}>
                  <th>{pagination?.from + index}</th>
                  <th>{item.id}</th>
                  <td>{item.title}</td>
                  <td className="text-center">
                    <Input
                      className="checkbox_animated"
                      id={`checkbox-${item.id}`}
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      onChange={() => handleCheckboxChange(item)}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="4" className="text-center">Data tidak ditemukan.</td></tr>
            )}
          </tbody>
        </Table>
        <CardBody className="mt-3">
          {pagination && pagination.total > pagination.per_page && (
            <PaginationComponent
              pagination={pagination}
              handleBackPagination={() => handleBackPagination(pagination, filters, dispatch, setFilters, setRefatch)}
              handleClickPagination={(page) => handleClickPagination(page, filters, dispatch, setFilters, setRefatch)}
              handleNextPagination={() => handleNextPagination(pagination, filters, dispatch, setFilters, setRefatch)}
            />
          )}
        </CardBody>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggleModal}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default ModalAddMembership;